import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { Toast } from 'primeng/toast';
import { Observable, Subscription } from 'rxjs';

import { IglesiasService } from '../../core/services/iglesias.service';
import { SolicitudesService } from '../../core/services/solicitudes.service';
import { UsersService } from '../../core/services/users.service';
import { ChatHeaderComponent } from '../../shared/components/chat/chat-header/chat-header.component';
import { ChatInputComponent } from '../../shared/components/chat/chat-input/chat-input.component';
import { ChatLayoutComponent } from '../../shared/components/chat/chat-layout/chat-layout.component';
import { ChatListComponent } from '../../shared/components/chat/chat-list/chat-list.component';
import { ChatMessagesComponent } from '../../shared/components/chat/chat-messages/chat-messages.component';
import { ordenarConversaciones } from '../../shared/helpers/chat-orden';
import { chatTienePendiente } from '../../shared/helpers/chat-tiene-pendiente';
import { ChatConversacion } from '../../shared/interfaces/chat.interface';
import { SolicitudBannerComponent } from './components/solicitud-banner/solicitud-banner.component';
import { SolicitudesDataSource } from './solicitudes-data-source';
import {
  DiscipulosAlMuelle,
  SIN_LIDER,
  SolicitudAccionCopy,
  SolicitudBannerAccion,
  solicitudAccionCopy,
} from './solicitudes.model';

@Component({
  selector: 'app-solicitudes',
  standalone: true,
  imports: [
    FormsModule,
    Button,
    ConfirmDialog,
    Dialog,
    Select,
    Textarea,
    Toast,
    ChatLayoutComponent,
    ChatListComponent,
    ChatHeaderComponent,
    ChatMessagesComponent,
    ChatInputComponent,
    SolicitudBannerComponent,
  ],
  providers: [{ provide: SolicitudesDataSource, useExisting: SolicitudesService }],
  templateUrl: './solicitudes.component.html',
  styleUrl: './solicitudes.component.scss',
})
export class SolicitudesComponent implements OnInit, OnDestroy {
  private readonly dataSource = inject(SolicitudesDataSource);
  private readonly usersService = inject(UsersService);
  private readonly iglesiasService = inject(IglesiasService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly messages = inject(MessageService);
  private readonly subscriptions = new Subscription();

  readonly mostrarNivel = signal(false);

  readonly conversaciones = signal<ChatConversacion[]>([]);
  readonly seleccionadaId = signal<string | null>(null);
  readonly loading = signal(false);
  /** En mobile el detalle tapa la lista. */
  readonly detalleAbierto = signal(false);

  readonly motivoVisible = signal(false);
  readonly motivoTexto = signal('');
  readonly motivoCopy = signal<SolicitudAccionCopy | null>(null);

  readonly avisoLiderVisible = signal(false);
  readonly elegirLiderVisible = signal(false);
  readonly discipulosTotal = signal(0);
  readonly muelleNombre = signal('');
  readonly lideresOpciones = signal<{ label: string; value: string }[]>([]);
  readonly liderDestino = signal<string | null>(null);
  readonly cargandoLideres = signal(false);

  private accionPendiente: SolicitudBannerAccion | null = null;
  private muellePersonaId: string | null = null;
  private motivoMuelle = '';

  readonly seleccionada = computed(
    () =>
      this.conversaciones().find(
        (conversacion) => conversacion.personaId === this.seleccionadaId(),
      ) ?? null,
  );

  readonly puedeAlMuelle = computed(() => this.seleccionada()?.idLider !== SIN_LIDER);

  readonly motivoInvalido = computed(() => this.motivoTexto().trim().length === 0);

  readonly textoDiscipulos = computed(() => {
    const total = this.discipulosTotal();
    if (total === 1) {
      return 'tiene 1 persona a cargo';
    }
    if (total > 1) {
      return `tiene ${total} personas a cargo`;
    }
    return 'tiene personas a cargo';
  });

  ngOnInit(): void {
    this.loading.set(true);
    this.subscriptions.add(
      this.iglesiasService.getCurrentIglesia().subscribe({
        next: (iglesia) => this.mostrarNivel.set((iglesia.setLevel ?? 0) !== 0),
        error: () => this.mostrarNivel.set(false),
      }),
    );
    this.subscriptions.add(
      this.dataSource.listar().subscribe({
        next: (lista) => {
          this.aplicar(lista);
          this.loading.set(false);
        },
        error: (err: HttpErrorResponse) => {
          this.loading.set(false);
          this.messages.add({
            severity: 'error',
            summary: 'Error',
            detail: this.mensajeError(err, 'No se pudieron cargar las solicitudes.'),
          });
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onSeleccionar(personaId: string): void {
    this.seleccionadaId.set(personaId);
    this.detalleAbierto.set(true);
    this.subscriptions.add(
      this.dataSource.marcarLeidos(personaId).subscribe((lista) => this.conversaciones.set(lista)),
    );
  }

  onVolver(): void {
    this.detalleAbierto.set(false);
  }

  onEnviar(nota: string): void {
    const personaId = this.seleccionadaId();
    if (!personaId) {
      return;
    }

    this.subscriptions.add(
      this.dataSource
        .enviarMensaje(personaId, nota)
        .subscribe((lista) => this.conversaciones.set(lista)),
    );
  }

  onCerrarChat(): void {
    const conversacion = this.seleccionada();
    if (!conversacion) {
      return;
    }

    if (chatTienePendiente(conversacion)) {
      this.messages.add({
        severity: 'warn',
        summary: 'Ups!',
        detail: 'Este chat tiene una solicitud pendiente. Debe resolverla para poder cerrarlo.',
      });
      return;
    }

    this.ejecutar(this.dataSource.cerrarChat(conversacion.personaId), 'Chat cerrado.');
  }

  onCerrarTodos(): void {
    this.confirmation.confirm({
      header: 'Cerrar chats',
      message: 'Se cierran todos los chats que no tengan una solicitud pendiente.',
      icon: 'pi pi-check-circle',
      acceptLabel: 'Cerrar chats',
      rejectLabel: 'Cancelar',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      accept: () => this.ejecutar(this.dataSource.cerrarChats(), 'Chats cerrados.'),
    });
  }

  onAccionSolicitud(evento: SolicitudBannerAccion): void {
    const conversacion = this.seleccionada();
    if (!conversacion) {
      return;
    }

    const copy = solicitudAccionCopy({
      tipo: evento.tipo,
      accion: evento.accion,
      nombre: `${conversacion.nombre} ${conversacion.apellido}`.trim(),
      nivelPersonal: conversacion.nivelPersonal,
    });
    if (!copy) {
      return;
    }

    if (copy.pideMotivo) {
      this.accionPendiente = evento;
      this.motivoCopy.set(copy);
      this.motivoTexto.set('');
      this.motivoVisible.set(true);
      return;
    }

    this.confirmation.confirm({
      header: copy.header,
      message: copy.mensaje,
      icon: copy.destructiva ? `${copy.icono} confirm-icon-papelera` : copy.icono,
      acceptLabel: copy.aceptar,
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: copy.destructiva ? 'p-button-danger' : '',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      accept: () =>
        this.ejecutar(
          this.dataSource.resolverSolicitud({ ...evento, personaId: conversacion.personaId }),
          copy.exito,
        ),
    });
  }

  confirmarMotivo(): void {
    const conversacion = this.seleccionada();
    const copy = this.motivoCopy();
    const evento = this.accionPendiente;
    if (!conversacion || !copy || !evento || this.motivoInvalido()) {
      return;
    }

    const motivo = this.motivoTexto().trim();
    const exito = copy.exito;
    const personaId = conversacion.personaId;
    const nombre = `${conversacion.nombre} ${conversacion.apellido}`.trim();
    this.cerrarMotivo();

    if (evento.tipo === 'eliminar' && evento.accion === 'muelle') {
      this.prepararMuelle(personaId, nombre, motivo);
      return;
    }

    this.ejecutar(
      this.dataSource.resolverSolicitud({
        ...evento,
        personaId,
        motivo,
      }),
      exito,
    );
  }

  dejarSinLider(): void {
    const personaId = this.muellePersonaId;
    const motivo = this.motivoMuelle;
    if (!personaId || !motivo) {
      return;
    }
    this.avisoLiderVisible.set(false);
    this.enviarAlMuelle(personaId, motivo, 'sin-lider');
  }

  abrirElegirLider(): void {
    this.liderDestino.set(null);
    this.elegirLiderVisible.set(true);
    this.avisoLiderVisible.set(false);
    this.cargarLideres();
  }

  volverAlAviso(): void {
    this.liderDestino.set(null);
    this.avisoLiderVisible.set(true);
    this.elegirLiderVisible.set(false);
  }

  confirmarTransferencia(): void {
    const personaId = this.muellePersonaId;
    const motivo = this.motivoMuelle;
    const idLiderDestino = this.liderDestino();
    if (!personaId || !motivo || !idLiderDestino) {
      return;
    }
    this.elegirLiderVisible.set(false);
    this.enviarAlMuelle(personaId, motivo, 'transferir', idLiderDestino);
  }

  cancelarMuelle(): void {
    this.avisoLiderVisible.set(false);
    this.elegirLiderVisible.set(false);
    this.limpiarMuelle();
  }

  onAvisoLiderVisibleChange(visible: boolean): void {
    if (!visible) {
      this.avisoLiderVisible.set(false);
      if (!this.elegirLiderVisible()) {
        this.limpiarMuelle();
      }
      return;
    }
    this.avisoLiderVisible.set(true);
  }

  onElegirLiderVisibleChange(visible: boolean): void {
    if (!visible) {
      this.elegirLiderVisible.set(false);
      this.liderDestino.set(null);
      if (!this.avisoLiderVisible()) {
        this.limpiarMuelle();
      }
      return;
    }
    this.elegirLiderVisible.set(true);
  }

  cerrarMotivo(): void {
    this.motivoVisible.set(false);
    this.motivoTexto.set('');
    this.motivoCopy.set(null);
    this.accionPendiente = null;
  }

  onMotivoVisibleChange(visible: boolean): void {
    if (!visible) {
      this.cerrarMotivo();
    }
  }

  private prepararMuelle(personaId: string, nombre: string, motivo: string): void {
    this.muellePersonaId = personaId;
    this.muelleNombre.set(nombre || 'Esta persona');
    this.motivoMuelle = motivo;
    this.subscriptions.add(
      this.dataSource.resumenDiscipulos(personaId).subscribe({
        next: (resumen) => {
          if (this.muellePersonaId !== personaId) {
            return;
          }
          if (resumen.lider && resumen.total > 0) {
            this.discipulosTotal.set(resumen.total);
            this.avisoLiderVisible.set(true);
            return;
          }
          this.enviarAlMuelle(personaId, motivo);
        },
        error: (err: HttpErrorResponse) => {
          this.limpiarMuelle();
          this.messages.add({
            severity: 'error',
            summary: 'Error',
            detail: this.mensajeError(err, 'No se pudo revisar si tiene discípulos.'),
          });
        },
      }),
    );
  }

  private cargarLideres(): void {
    const personaId = this.muellePersonaId;
    this.cargandoLideres.set(true);
    this.lideresOpciones.set([]);
    this.subscriptions.add(
      this.usersService.getLideres().subscribe((lideres) => {
        if (this.muellePersonaId !== personaId) {
          return;
        }
        this.lideresOpciones.set(
          lideres
            .filter((lider) => lider.id !== personaId)
            .map((lider) => ({
              label: `${lider.nombre} ${lider.apellido}`.trim(),
              value: lider.id,
            })),
        );
        this.cargandoLideres.set(false);
      }),
    );
  }

  private enviarAlMuelle(
    personaId: string,
    motivo: string,
    discipulos?: DiscipulosAlMuelle,
    idLiderDestino?: string,
  ): void {
    const detail =
      discipulos === 'transferir'
        ? 'Discípulos transferidos. Persona enviada al muelle.'
        : discipulos === 'sin-lider'
          ? 'Los discípulos quedaron sin líder. Persona enviada al muelle.'
          : 'Persona enviada al muelle.';

    this.subscriptions.add(
      this.dataSource
        .resolverSolicitud({
          tipo: 'eliminar',
          accion: 'muelle',
          personaId,
          motivo,
          discipulos,
          idLiderDestino,
        })
        .subscribe({
          next: (lista) => {
            this.limpiarMuelle();
            this.aplicar(lista);
            this.messages.add({ severity: 'success', summary: 'Listo', detail });
          },
          error: (err: HttpErrorResponse) => {
            const message = this.mensajeError(err, 'No se pudo completar la acción.');
            if (!discipulos && err.status === 409 && message.toLowerCase().includes('discípulos')) {
              this.avisoLiderVisible.set(true);
              return;
            }
            this.limpiarMuelle();
            this.messages.add({
              severity: 'error',
              summary: 'Error',
              detail: message,
            });
          },
        }),
    );
  }

  private limpiarMuelle(): void {
    this.muellePersonaId = null;
    this.motivoMuelle = '';
    this.muelleNombre.set('');
    this.discipulosTotal.set(0);
    this.liderDestino.set(null);
    this.lideresOpciones.set([]);
    this.cargandoLideres.set(false);
    this.avisoLiderVisible.set(false);
    this.elegirLiderVisible.set(false);
  }

  private ejecutar(operacion: Observable<ChatConversacion[]>, exito: string): void {
    this.subscriptions.add(
      operacion.subscribe({
        next: (lista) => {
          this.aplicar(lista);
          this.messages.add({ severity: 'success', summary: 'Listo', detail: exito });
        },
        error: (err: HttpErrorResponse) => {
          this.messages.add({
            severity: 'error',
            summary: 'Error',
            detail: this.mensajeError(err, 'No se pudo completar la acci\u00f3n.'),
          });
        },
      }),
    );
  }

  /** Reselecciona el chat mas reciente cuando el que estaba abierto ya no esta en la lista. */
  private aplicar(lista: ChatConversacion[]): void {
    this.conversaciones.set(lista);

    const actual = this.seleccionadaId();
    if (actual && lista.some((conversacion) => conversacion.personaId === actual)) {
      return;
    }

    const siguiente = ordenarConversaciones(lista)[0]?.personaId ?? null;
    this.seleccionadaId.set(siguiente);
    this.detalleAbierto.set(false);
  }

  private mensajeError(err: HttpErrorResponse, fallback: string): string {
    const message = err.error?.message;
    return typeof message === 'string' && message.trim() ? message : fallback;
  }
}
